import { useState } from "react";
import { Alert, Input, Modal, Radio } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import cdrReviewApi, { CdrReviewApiError } from "@/services/cdrReviewApi";
import { useAppNotification } from "@/hooks/useAppNotification";
import type {
  CdrDispositionAction,
  CdrReviewItemSummary,
} from "./clinicalReview.types";

const RATIONALE_REQUIRED: CdrDispositionAction[] = ["observation", "escalated"];

const ACTION_OPTIONS: { value: CdrDispositionAction; label: string }[] = [
  { value: "reviewed_no_issue", label: "Reviewed – no issue" },
  { value: "observation", label: "Observation" },
  { value: "escalated", label: "Escalated" },
];

type Props = {
  trialId: string;
  item: CdrReviewItemSummary | null;
  onClose: () => void;
  onDispositioned: () => void;
};

export default function ClinicalReviewDispositionModal({
  trialId,
  item,
  onClose,
  onDispositioned,
}: Props) {
  const [action, setAction] =
    useState<CdrDispositionAction>("reviewed_no_issue");
  const [comment, setComment] = useState("");
  const queryClient = useQueryClient();
  const { openSuccessNotification, openErrorNotification, contextHolder } =
    useAppNotification();

  const rationaleRequired = RATIONALE_REQUIRED.includes(action);
  const rationaleMissing = rationaleRequired && !comment.trim();

  const markMutation = useMutation({
    mutationFn: () => {
      if (!item) throw new Error("No item selected");
      return cdrReviewApi.mark(trialId, {
        items: [{ item_id: item.id, material_hash: item.material_hash }],
        action,
        comment: comment.trim(),
      });
    },
    onSuccess: (result) => {
      if (result.stale.length > 0) {
        openErrorNotification({
          title: "Record changed since you opened it",
          description:
            "This record was updated by a scan since you opened it. Reopen it to review the current data before dispositioning.",
        });
        onClose();
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["cdr-review-item", trialId] });
      const escalation = result.escalations[0];
      openSuccessNotification({
        title: "Disposition recorded",
        description: escalation
          ? escalation.created
            ? `Escalated. A new discrepancy (${escalation.issue_id}) was created for the DM queue.`
            : `Escalated. Linked to the existing discrepancy ${escalation.issue_id}.`
          : "The record's clinical data review status has been updated.",
      });
      setComment("");
      setAction("reviewed_no_issue");
      onClose();
      onDispositioned();
    },
    onError: (error: CdrReviewApiError) =>
      openErrorNotification({
        title: "Could not record the disposition",
        description: error.message,
      }),
  });

  return (
    <Modal
      title={item ? `Disposition — ${item.display_id}` : "Disposition"}
      open={item !== null}
      onCancel={onClose}
      okText="Record disposition"
      okButtonProps={{
        disabled: rationaleMissing,
        loading: markMutation.isPending,
      }}
      onOk={() => markMutation.mutate()}
      destroyOnHidden
    >
      {contextHolder}
      <div className="flex flex-col gap-3">
        {item && <p className="m-0 text-xs text-[#556C75]">{item.summary}</p>}
        <Radio.Group
          value={action}
          onChange={(event) => setAction(event.target.value)}
          className="flex flex-col gap-2"
        >
          {ACTION_OPTIONS.map((option) => (
            <Radio key={option.value} value={option.value}>
              {option.label}
            </Radio>
          ))}
        </Radio.Group>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-[#2B4752]">
            Rationale{rationaleRequired ? " (required)" : " (optional)"}
          </span>
          <Input.TextArea
            rows={3}
            maxLength={4000}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Why this disposition — e.g. plausible given concurrent AE burden, looks like a transcription error, matches a known rater effect."
          />
        </label>
        {rationaleMissing && (
          <Alert
            type="warning"
            showIcon
            message="A rationale is required for Observation and Escalated — an undocumented finding is itself an audit finding."
          />
        )}
        {action === "escalated" && (
          <Alert
            type="info"
            showIcon
            message="Escalating links this record to an open data-quality discrepancy, or creates one if none exists yet. It reaches DM's existing queue, origin-tagged as Clinical Data Review."
          />
        )}
      </div>
    </Modal>
  );
}
